import React, { useState } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { PaymentData } from '../types';
import { Investment } from '../../../types';
import { DollarSign, TrendingUp, Calendar, BarChart3, Target, Award } from 'lucide-react';

interface ReturnsSimulatorTabProps {
  paymentData: PaymentData;
  investment: Investment;
}

const ReturnsSimulatorTab: React.FC<ReturnsSimulatorTabProps> = ({ paymentData, investment }) => {
  const [selectedPeriod, setSelectedPeriod] = useState<'annual' | 'monthly'>('annual');
  const [selectedYear, setSelectedYear] = useState<number>(1);

  // Calculate detailed returns data following the structure from the image
  const calculateDetailedReturns = () => {
    const returns = [];
    const initialPrice = investment.certificateBasePrice;
    const appreciationRate = investment.appreciationRate / 100; // 12% annual
    const lemonPriceIncrease = investment.lemonPriceIncrease / 100; // 5% annual after year 5
    const averageProductionPerHectare = investment.averageProductionPerHectare;
    const averageSalePricePerKg = investment.averageSalePricePerKg;
    
    let cumulativeOperationalReturn = 0;
    let cumulativeCapitalGain = 0;
    let previousCertificatePrice = initialPrice;
    
    for (let year = 1; year <= 30; year++) {
      // Calculate certificate price for this year
      let certificatePrice: number;
      if (year <= 5) {
        // First 5 years: 12% annual appreciation
        certificatePrice = initialPrice * Math.pow(1 + appreciationRate, year);
      } else {
        // After year 5: max appreciated value + lemon price increase
        const maxAppreciatedValue = initialPrice * Math.pow(1 + appreciationRate, 5);
        if (investment.increaseLemonPrice) {
          certificatePrice = maxAppreciatedValue * Math.pow(1 + lemonPriceIncrease, year - 5);
        } else {
          certificatePrice = maxAppreciatedValue;
        }
      }
      
      // Calculate operational return (only after year 4)
      let annualOperationalReturn = 0;
      if (year > 4) {
        // Calculate current lemon price with increase
        let currentLemonPrice = averageSalePricePerKg;
        if (year > 5 && investment.increaseLemonPrice) {
          currentLemonPrice = averageSalePricePerKg * Math.pow(1 + lemonPriceIncrease, year - 5);
        }
        
        // Annual utility per certificate: production × price × 0.1 × 0.65
        annualOperationalReturn = averageProductionPerHectare * currentLemonPrice * 0.1 * 0.65;
      }
      
      // Calculate capital gain (plusvalía) for this year
      const annualCapitalGain = certificatePrice - previousCertificatePrice;
      
      // Update cumulative values
      cumulativeOperationalReturn += annualOperationalReturn;
      cumulativeCapitalGain += annualCapitalGain;
      
      // Calculate total accumulated utility
      const totalAccumulatedUtility = cumulativeOperationalReturn + cumulativeCapitalGain;
      
      returns.push({
        year,
        annualOperationalReturn,
        cumulativeOperationalReturn,
        annualCapitalGain,
        cumulativeCapitalGain,
        totalAccumulatedUtility,
        certificatePrice,
        currentLemonPrice: year > 5 && investment.increaseLemonPrice ? 
          averageSalePricePerKg * Math.pow(1 + lemonPriceIncrease, year - 5) : 
          averageSalePricePerKg
      });
      
      previousCertificatePrice = certificatePrice;
    }
    
    return returns;
  };

  const detailedReturns = calculateDetailedReturns();
  
  // Calculate CAGR for certificate appreciation
  const initialValue = investment.certificateBasePrice;
  const finalValue = detailedReturns[detailedReturns.length - 1]?.certificatePrice || initialValue;
  const cagr = Math.pow(finalValue / initialValue, 1 / 30) - 1;

  return (
    <div className="space-y-8">
      {/* Summary Cards */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Simulador de Rendimientos y Plusvalía</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
            <div className="text-sm text-green-600 mb-1">Rendimiento Total (30 años)</div>
            <div className="text-xl font-bold text-green-700">
              {formatCurrency(
                detailedReturns[detailedReturns.length - 1]?.cumulativeOperationalReturn || 0,
                investment.currencyFormat
              )}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
            <div className="text-sm text-blue-600 mb-1">Plusvalía Total (30 años)</div>
            <div className="text-xl font-bold text-blue-700">
              {formatCurrency(
                detailedReturns[detailedReturns.length - 1]?.cumulativeCapitalGain || 0,
                investment.currencyFormat
              )}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="text-sm text-purple-600 mb-1">Utilidad Total Acumulada</div>
            <div className="text-xl font-bold text-purple-700">
              {formatCurrency(
                detailedReturns[detailedReturns.length - 1]?.totalAccumulatedUtility || 0,
                investment.currencyFormat
              )}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
            <div className="text-sm text-orange-600 mb-1">CAGR Apreciación</div>
            <div className="text-xl font-bold text-orange-700">
              {(cagr * 100).toFixed(1)}%
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-xl">
            <div className="text-sm text-teal-600 mb-1">Valor Final Certificado</div>
            <div className="text-xl font-bold text-teal-700">
              {formatCurrency(finalValue, investment.currencyFormat)}
            </div>
          </div>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setSelectedPeriod('annual')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              selectedPeriod === 'annual'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Vista Anual (30 años)
          </button>
          <button
            onClick={() => setSelectedPeriod('monthly')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              selectedPeriod === 'monthly'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Vista Mensual Detallada
          </button>
        </div>

        {selectedPeriod === 'monthly' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Año para Vista Mensual
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              {Array.from({ length: 30 }, (_, i) => i + 1).map(year => (
                <option key={year} value={year}>
                  Año {year}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Annual Summary Table - Following the exact structure from the image */}
      {selectedPeriod === 'annual' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Tabla de Rendimientos y Plusvalía (30 Años)</h3>
          </div>
          
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    Año
                  </th>
                  <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider bg-green-600 border-r border-gray-300">
                    Rendimientos
                  </th>
                  <th colSpan={2} className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider bg-blue-600 border-r border-gray-300">
                    Plusvalía
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider bg-purple-600">
                    Utilidad Acumulada
                  </th>
                </tr>
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">
                    
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                    Rendimiento Anual
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50 border-r border-gray-300">
                    Rendimiento Acumulado
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                    Plusvalía Anual
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50 border-r border-gray-300">
                    Plusvalía Acumulada
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50">
                    Utilidad Acumulada Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {detailedReturns.map((data, index) => (
                  <tr key={index} className={`hover:bg-gray-50 ${
                    data.year === 5 ? 'bg-yellow-50' : 
                    data.year === 10 ? 'bg-blue-50' : 
                    data.year === 20 ? 'bg-purple-50' : 
                    data.year === 30 ? 'bg-green-50' : ''
                  }`}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-center border-r border-gray-200">
                      Año {data.year}
                    </td>
                    
                    {/* Rendimientos */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600 bg-green-25">
                      {data.annualOperationalReturn > 0 ? 
                        formatCurrency(data.annualOperationalReturn, investment.currencyFormat) : 
                        '-'
                      }
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-700 bg-green-25 border-r border-gray-200">
                      {data.cumulativeOperationalReturn > 0 ? 
                        formatCurrency(data.cumulativeOperationalReturn, investment.currencyFormat) : 
                        '-'
                      }
                    </td>
                    
                    {/* Plusvalía */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600 bg-blue-25">
                      {formatCurrency(data.annualCapitalGain, investment.currencyFormat)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-blue-700 bg-blue-25 border-r border-gray-200">
                      {formatCurrency(data.cumulativeCapitalGain, investment.currencyFormat)}
                    </td>
                    
                    {/* Utilidad Acumulada Total */}
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-bold text-purple-700 bg-purple-25">
                      {formatCurrency(data.totalAccumulatedUtility, investment.currencyFormat)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Key Milestones */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-yellow-600" />
                <h4 className="font-medium text-yellow-800">Año 5 - Fin Apreciación</h4>
              </div>
              <div className="text-lg font-bold text-yellow-700">
                {formatCurrency(detailedReturns[4]?.certificatePrice || 0, investment.currencyFormat)}
              </div>
              <div className="text-sm text-yellow-600">Valor del certificado</div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <h4 className="font-medium text-green-800">Año 5 - Inicio Producción</h4>
              </div>
              <div className="text-lg font-bold text-green-700">
                {formatCurrency(detailedReturns[4]?.annualOperationalReturn || 0, investment.currencyFormat)}
              </div>
              <div className="text-sm text-green-600">Primer rendimiento anual</div>
            </div>
            
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-600" />
                <h4 className="font-medium text-blue-800">Año 10</h4>
              </div>
              <div className="text-lg font-bold text-blue-700">
                {formatCurrency(detailedReturns[9]?.totalAccumulatedUtility || 0, investment.currencyFormat)}
              </div>
              <div className="text-sm text-blue-600">Utilidad acumulada</div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-5 h-5 text-purple-600" />
                <h4 className="font-medium text-purple-800">Año 30 - Final</h4>
              </div>
              <div className="text-lg font-bold text-purple-700">
                {formatCurrency(detailedReturns[29]?.totalAccumulatedUtility || 0, investment.currencyFormat)}
              </div>
              <div className="text-sm text-purple-600">Utilidad total acumulada</div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Detail for Selected Year */}
      {selectedPeriod === 'monthly' && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800">Detalle Mensual - Año {selectedYear}</h3>
          </div>
          
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rendimiento Mensual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Plusvalía Mensual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilidad Total Mensual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Certificado
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paymentData.monthlyFinancials
                  .filter(data => data.year === selectedYear)
                  .map((data, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {data.month - (data.year - 1) * 12}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {data.date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-green-600">
                        {formatCurrency(data.operationalReturn, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                        {formatCurrency(data.capitalAppreciation, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-purple-600">
                        {formatCurrency(data.totalReturn, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900">
                        {formatCurrency(data.certificateBuybackPrice, investment.currencyFormat)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Calculation Notes */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800">Notas de Cálculo</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-green-800 mb-2">Rendimientos Operativos</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Inician en el año 5 (después de maduración)</li>
              <li>• Basados en: {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg</li>
              <li>• Factor de participación: 10% × 65% = 6.5%</li>
              <li>• {investment.increaseLemonPrice ? `Incremento precio limón: ${investment.lemonPriceIncrease}% anual` : 'Sin incremento en precio del limón'}</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-medium text-blue-800 mb-2">Plusvalía (Apreciación)</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Años 1-5: {investment.appreciationRate}% anual</li>
              <li>• Años 6-30: {investment.increaseLemonPrice ? `${investment.lemonPriceIncrease}% anual` : 'Sin incremento'}</li>
              <li>• Precio inicial: {formatCurrency(investment.certificateBasePrice, investment.currencyFormat)}</li>
              <li>• Precio final proyectado: {formatCurrency(finalValue, investment.currencyFormat)}</li>
            </ul>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-gray-50 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Utilidad Acumulada Total:</strong> Suma de todos los rendimientos operativos y plusvalía generados desde el año 1 hasta el año correspondiente.
            Esta métrica muestra el beneficio total acumulado de la inversión en cada punto del tiempo.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReturnsSimulatorTab;